import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import GlobalSearchInput from "../GlobalSearch/GlobalSearchInput";
import LanguageMenu from "../LanguageMenu";
import { useRewampLocalization, useRewampT } from "../../localisation";
import { applyNavHeight } from "../../layouts/navMetrics";
import { PWA_CHROME } from "../../layouts/pwaConfig";
import PwaAccountDrawer from "./PwaAccountDrawer";

const NAMASTE = "\u{1F64F}\u{1F3FC}";

// The bar's height changes with text size and the voice error line, so sticky offsets follow the measured height.
const useMeasuredNavHeight = (ref, toolBar) => {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const write = () => applyNavHeight(PWA_CHROME.home, el.offsetHeight);
    write();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(write);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, toolBar]);
};

const PwaWelcomeRow = () => {
  const tRewamp = useRewampT();
  const location = useLocation();
  const firstName = useSelector((state) => state.profileData?.name)?.trim().split(/\s+/)[0];
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search, location.key]);

  return (
    <Box sx={(theme) => ({ display: "flex", alignItems: "center", columnGap: 1.5, px: 2, height: theme.spacing(3) })}>
      <IconButton
        size="small"
        onClick={() => setDrawerOpen(true)}
        aria-label={tRewamp("PWA_APPBAR.PROFILE_MENU")}
        aria-haspopup="dialog"
        aria-expanded={drawerOpen}
        sx={(theme) => ({
          p: 0,
          width: theme.spacing(3),
          height: theme.spacing(3),
          color: "primary.interactive",
          "&:focus-visible": { outline: `3px solid ${theme.palette.accent.butterscotch}`, outlineOffset: theme.spacing(0.25) },
        })}
      >
        <AccountCircleIcon aria-hidden="true" sx={(theme) => ({ width: theme.spacing(3), height: theme.spacing(3) })} />
      </IconButton>
      <Typography
        variant="subtitle2"
        component="p"
        noWrap
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          columnGap: 1.5,
          minWidth: 0,
          m: 0,
          height: theme.spacing(3),
          color: "neutral.headingsStrong",
          fontSize: theme.typography.pxToRem(16),
          fontWeight: 700,
          lineHeight: theme.spacing(3),
        })}
      >
        <Typography variant="inherit" component="span" noWrap>
          {NAMASTE} {firstName || tRewamp("PWA_APPBAR.WELCOME_PLAIN")}
        </Typography>
      </Typography>
      <PwaAccountDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </Box>
  );
};

const PwaToolBar = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <GlobalSearchInput appearance="pill" />
    </Box>
    <LanguageMenu />
  </Box>
);

const PwaAppBar = ({ toolBar = true, showWelcome = true }) => {
  useRewampLocalization(["common"]);
  const ref = useRef(null);
  useMeasuredNavHeight(ref, toolBar || showWelcome);

  if (!showWelcome && !toolBar) return null;

  return (
    <Box
      ref={ref}
      component="header"
      sx={{ position: "sticky", top: 0, zIndex: "appBar", backgroundColor: "neutral.surface" }}
    >
      {showWelcome ? <PwaWelcomeRow /> : null}
      {toolBar ? <PwaToolBar /> : null}
    </Box>
  );
};

PwaAppBar.propTypes = {
  toolBar: PropTypes.bool,
  showWelcome: PropTypes.bool,
};

export default PwaAppBar;
